// SalesDayPage.jsx - Version améliorée
import React, { useEffect, useState, useMemo } from "react";
import salesStore from "../../store/salesStore";
import {
  BiCalendar,
  BiMoney,
  BiShoppingBag,
  BiUser,
  BiUserX,
  BiSearch,
  BiChevronLeft,
  BiChevronRight,
  BiDetail,
  BiPhone,
} from "react-icons/bi";
import { FaEnvelope, FaWhatsapp } from "react-icons/fa";
import { MdAppRegistration, MdSubscriptions } from "react-icons/md";
import { TbMoneybag, TbPigMoney } from "react-icons/tb";

function formatFrenchDateCapitalized(isoDate) {
  const date = new Date(isoDate);
  const formatted = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

// Composant Modal de Détails (inchangé)
function PaymentDetailModal({ isOpen, onClose, payment }) {
  if (!isOpen || !payment) return null;

  const getPaymentType = (payment) => {
    if (payment.is_vente)
      return {
        label: "Vente",
        color: "bg-green-100 text-green-800",
        icon: <BiShoppingBag />,
      };
    if (payment.appointment)
      return {
        label: "Réservation",
        color: "bg-blue-100 text-blue-800",
        icon: <MdAppRegistration />,
      };
    if (payment.subscription)
      return {
        label: "Abonnement",
        color: "bg-purple-100 text-purple-800",
        icon: <MdSubscriptions />,
      };
    return { label: "Autre", color: "bg-gray-100 text-gray-800", icon: null };
  };

  const paymentType = getPaymentType(payment);
  const client = payment.extraData || payment.user;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="font-poppins font-semibold text-xl text-title">
              Détails du paiement #{payment.code}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`px-3 py-1 text-sm font-medium ${paymentType.color} flex items-center gap-1`}
              >
                {paymentType.icon}
                {paymentType.label}
              </span>
              <span
                className={`px-3 py-1 text-sm font-medium ${
                  payment.method === "CASH"
                    ? "bg-green-100 text-green-800"
                    : payment.method === "MOBILE_MONEY"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-secondary-100 text-secondary-800"
                }`}
              >
                {payment.method}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Client
              </h3>
              {client ? (
                <div className="space-y-3">
                  <div>
                    <p className="font-nunito text-sm text-text">Nom complet</p>
                    <p className="font-poppins font-medium">
                      {client.prenom} {client.nom}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Contact</p>
                    <div className="flex gap-3 mt-2">
                      {client.email && (
                        <a
                          href={`mailto:${client.email}`}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          <FaEnvelope size={20} />
                        </a>
                      )}
                      {client.telephone && (
                        <a
                          href={`tel:${client.telephone}`}
                          className="text-secondary-600 hover:text-secondary-800"
                        >
                          <BiPhone size={20} />
                        </a>
                      )}
                      {(client.whatsapp || client.tel) && (
                        <a
                          href={`https://wa.me/${client.whatsapp || client.tel}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-800"
                        >
                          <FaWhatsapp size={20} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="font-nunito text-text">Client non enregistré</p>
              )}
            </div>

            <div className="bg-gray-50 p-4">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Paiement
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="font-nunito text-sm text-text">Montant</p>
                  <p className="font-poppins font-bold text-xl text-montant">
                    {parseFloat(payment.amount).toLocaleString()} FCFA
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Date</p>
                  <p className="font-poppins font-medium">
                    {new Date(payment.created_at).toLocaleString("fr-FR")}
                  </p>
                </div>
                <div>
                  <p className="font-nunito text-sm text-text">Statut</p>
                  <span
                    className={`px-2 py-1 text-sm ${
                      payment.status === "COMPLETED"
                        ? "bg-green-100 text-green-800"
                        : payment.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                    }`}
                  >
                    {payment.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                Services ({payment.services.length})
              </h3>
              <div className="space-y-3">
                {payment.services.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white border"
                  >
                    <div>
                      <p className="font-poppins font-medium">
                        {item.service.nom}
                      </p>
                      <p className="font-nunito text-sm text-text">
                        {item.service.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-poppins font-bold text-montant">
                        {parseFloat(item.unitPrice).toLocaleString()} FCFA
                      </p>
                      <p className="font-nunito text-sm text-text">
                        Quantité: {item.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {payment.appointment && (
              <div className="md:col-span-2 bg-blue-50 p-4">
                <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                  Détails de la réservation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="font-nunito text-sm text-text">
                      Date du rendez-vous
                    </p>
                    <p className="font-poppins font-medium">
                      {new Date(payment.appointment.date).toLocaleString(
                        "fr-FR",
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">
                      Durée estimée
                    </p>
                    <p className="font-poppins font-medium">
                      {payment.appointment.estimatedDuration} minutes
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Statut</p>
                    <span
                      className={`px-2 py-1 text-sm ${
                        payment.appointment.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : payment.appointment.status === "CONFIRMED"
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {payment.appointment.status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {payment.subscription && (
              <div className="md:col-span-2 bg-purple-50 p-4">
                <h3 className="font-poppins font-semibold text-lg text-title mb-3">
                  Détails de l'abonnement
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="font-nunito text-sm text-text">Formule</p>
                    <p className="font-poppins font-medium">
                      {payment.subscription.plan.name}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Description</p>
                    <p className="font-poppins font-medium">
                      {payment.subscription.plan.description}
                    </p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-text">Statut</p>
                    <span
                      className={`px-2 py-1 text-sm ${
                        payment.subscription.status === "ACTIVE"
                          ? "bg-green-100 text-green-800"
                          : payment.subscription.status === "EXPIRED"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {payment.subscription.status}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 font-nunito hover:bg-gray-50"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Composant Card pour mobile - Version améliorée selon vos suggestions
function PaymentCard({ payment, onViewDetails }) {
  const getPaymentType = (payment) => {
    if (payment.is_vente)
      return {
        label: "Vente",
        color: "bg-green-100 text-green-800",
        icon: <BiShoppingBag size={12} />,
      };
    if (payment.appointment)
      return {
        label: "Réservation",
        color: "bg-blue-100 text-blue-800",
        icon: <MdAppRegistration size={12} />,
      };
    if (payment.subscription)
      return {
        label: "Abonnement",
        color: "bg-purple-100 text-purple-800",
        icon: <MdSubscriptions size={12} />,
      };
    return { label: "Autre", color: "bg-gray-100 text-gray-800", icon: null };
  };

  const paymentType = getPaymentType(payment);
  const client = payment.extraData || payment.user;

  // Limiter le nom à 20 caractères
  const clientName = client
    ? `${client.prenom} ${client.nom}`
    : "Client non enregistré";
  const displayName =
    clientName.length > 20 ? clientName.substring(0, 20) + "..." : clientName;

  return (
    <div className="bg-white border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
      {/* Ligne 1: Nom client + Type paiement + Méthode */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="font-nunito font-medium text-sm truncate"
              title={clientName}
            >
              {displayName}
            </span>
            <span
              className={`px-2 py-1 text-xs font-medium ${paymentType.color} flex items-center gap-1 shrink-0`}
            >
              {paymentType.icon}
              {paymentType.label}
            </span>
          </div>
          <span
            className={`px-2 py-1 text-xs ${
              payment.method === "CASH"
                ? "bg-green-100 text-green-800"
                : payment.method === "MOBILE_MONEY"
                  ? "bg-purple-100 text-purple-800"
                  : "bg-secondary-100 text-secondary-800"
            } shrink-0`}
          >
            {payment.method}
          </span>
        </div>

        {/* Ligne 2: Montant */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BiMoney className="text-gray-400" size={16} />
            <span className="font-poppins font-bold text-base text-montant">
              {parseFloat(payment.amount).toLocaleString()} FCFA
            </span>
          </div>
        </div>
      </div>

      {/* Ligne 3: Services + Date + Contacts */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <BiShoppingBag size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {payment.services?.length || 0} service
                {payment.services?.length > 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <BiCalendar size={14} className="text-gray-500" />
              <span className="font-nunito text-xs text-text">
                {new Date(payment.created_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {client?.email && (
              <a
                href={`mailto:${client.email}`}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaEnvelope size={14} />
              </a>
            )}
            {client?.telephone && (
              <a
                href={`tel:${client.telephone}`}
                className="text-secondary-500 hover:text-secondary-700"
              >
                <BiPhone size={14} />
              </a>
            )}
            {(client?.whatsapp || client?.tel) && (
              <a
                href={`https://wa.me/${client.whatsapp || client.tel}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 hover:text-green-700"
              >
                <FaWhatsapp size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Ligne 4: Bouton Détails pleine largeur */}
      <div className="p-3 bg-gray-50">
        <button
          onClick={() => onViewDetails(payment)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          <BiDetail size={16} />
          Voir les détails
        </button>
      </div>
    </div>
  );
}

export default function SalesDayPage() {
  const {
    allPayments: dailySummary,
    isLoadingPayments: isLoadingDailySummary,
    getAllPayments: getDailySummary,
  } = salesStore();

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    console.log({ selectedDate });
    getDailySummary(selectedDate);
  }, [selectedDate, getDailySummary]);

  const totalRevenue = dailySummary.reduce((total, payment) => {
    return total + parseFloat(payment.amount);
  }, 0);

  // const totalSummary = dailySummary.length;

  const totalVente = dailySummary.filter(
    (payment) => payment.is_vente === true,
  ).length;

  const totalAppointment = dailySummary.filter(
    (payment) => payment.appointment !== null,
  ).length;

  const totalSubscription = dailySummary.filter(
    (payment) => payment.subscription !== null,
  ).length;

  // const totalClient = dailySummary.filter(
  //   (payment) => payment.user != null,
  // ).length;

  // const totalClientExtra = dailySummary.filter(
  //   (payment) => payment.extraData != null,
  // ).length;

  const totalCash = dailySummary.filter(
    (payment) => payment.method === "CASH",
  ).length;

  // const cashAmount = dailySummary.reduce((total, payment) => {
  //   return payment.method === "CASH"
  //     ? total + parseFloat(payment.amount)
  //     : total + 0;
  // }, 0);

  // const cashPurcent = Math.round((totalCash * 100) / totalSummary);

  const totalFedapay = dailySummary.filter(
    (payment) => payment.method === "FEDAPAY",
  ).length;

  // const fedapayAmount = dailySummary.reduce((total, payment) => {
  //   return payment.method === "FEDAPAY"
  //     ? total + parseFloat(payment.amount)
  //     : total + 0;
  // }, 0);

  // const fedapayPurcent = Math.round((totalFedapay * 100) / totalSummary);

  const totalMomo = dailySummary.filter(
    (payment) => payment.method === "MOBILE_MONEY",
  ).length;

  // const momoAmount = dailySummary.reduce((total, payment) => {
  //   return payment.method === "MOBILE_MONEY"
  //     ? total + parseFloat(payment.amount)
  //     : total + 0;
  // }, 0);

  // const momoPurcent = Math.round((totalMomo * 100) / totalSummary);

  const getPaymentType = (payment) => {
    if (payment.is_vente) return "Vente";
    if (payment.appointment) return "Réservation";
    if (payment.subscription) return "Abonnement";
    return "Autre";
  };

  const filteredPayments = useMemo(() => {
    if (!dailySummary) return [];

    return dailySummary.filter((payment) => {
      const client = payment.extraData || payment.user;
      const clientName = client
        ? `${client.prenom} ${client.nom}`.toLowerCase()
        : "";
      const paymentType = getPaymentType(payment).toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      return (
        clientName.includes(searchLower) ||
        payment.code.toLowerCase().includes(searchLower) ||
        paymentType.includes(searchLower) ||
        payment.method.toLowerCase().includes(searchLower)
      );
    });
  }, [dailySummary, searchTerm]);

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setIsModalOpen(true);
  };

  // Colonnes pour le tableau desktop - Version simplifiée
  const columns = [
    {
      headerName: "Client",
      width: "25%",
      render: (payment) => {
        const client = payment.extraData || payment.user;
        const clientName = client
          ? `${client.prenom} ${client.nom}`
          : "Client non enregistré";
        const displayName =
          clientName.length > 25
            ? clientName.substring(0, 25) + "..."
            : clientName;

        return (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className="font-nunito font-normal text-sm truncate"
                title={clientName}
              >
                {displayName}
              </span>
              <div className="flex gap-1 shrink-0">
                {client?.email && (
                  <a
                    href={`mailto:${client.email}`}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaEnvelope size={12} />
                  </a>
                )}
                {client?.telephone && (
                  <a
                    href={`tel:${client.telephone}`}
                    className="text-secondary-500 hover:text-secondary-700"
                  >
                    <BiPhone size={12} />
                  </a>
                )}
                {(client?.whatsapp || client?.tel) && (
                  <a
                    href={`https://wa.me/${client.whatsapp || client.tel}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-500 hover:text-green-700"
                  >
                    <FaWhatsapp size={14} />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      headerName: "Type / Méthode",
      width: "20%",
      render: (payment) => {
        const type = getPaymentType(payment);
        const getTypeStyles = () => {
          switch (type) {
            case "Vente":
              return "bg-green-100 text-green-800";
            case "Réservation":
              return "bg-blue-100 text-blue-800";
            case "Abonnement":
              return "bg-purple-100 text-purple-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        };

        return (
          <div className="flex flex-col gap-1">
            <span
              className={`px-2 py-1 text-xs font-medium ${getTypeStyles()} w-fit`}
            >
              {type}
            </span>
            <span
              className={`px-2 py-1 text-xs ${
                payment.method === "CASH"
                  ? "bg-green-100 text-green-800"
                  : payment.method === "MOBILE_MONEY"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-secondary-100 text-secondary-800"
              } w-fit`}
            >
              {payment.method}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Montant",
      width: "15%",
      render: (payment) => (
        <div className="text-sm text-montant">
          {parseFloat(payment.amount).toLocaleString()} FCFA
        </div>
      ),
    },
    {
      headerName: "Services",
      width: "10%",
      render: (payment) => (
        <div className="flex items-center gap-1">
          <BiShoppingBag size={14} className="text-gray-500" />
          <span className="font-nunito text-xs">
            {payment.services?.length || 0}
          </span>
        </div>
      ),
    },
    {
      headerName: "Date",
      width: "15%",
      render: (payment) => (
        <div className="font-nunito text-xs text-text">
          {new Date(payment.created_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      headerName: "",
      width: "15%",
      render: (payment) => (
        <button
          onClick={() => handleViewDetails(payment)}
          className="w-full px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
        >
          <BiDetail size={14} />
          Détails
        </button>
      ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
        <div>
          <h1 className="font-poppins font-semibold text-lg text-title">
            Résumé Quotidien des Ventes
          </h1>
          <p className="font-nunito text-sm text-text mt-1">
            Aperçu des performances commerciales pour{" "}
            {formatFrenchDateCapitalized(selectedDate)}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <div className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2">
            <BiCalendar className="text-gray-400" size={18} />
            <input
              type="date"
              value={selectedDate}
              max={new Date()}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="focus:outline-none font-nunito text-sm bg-transparent"
            />
          </div>
        </div>
      </div>

      {isLoadingDailySummary ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary-600"></div>
        </div>
      ) : (
        <>
          {/* Cartes de statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              {
                title: "Chiffre d'affaires",
                value: `${totalRevenue.toLocaleString()} FCFA`,
                icon: <BiMoney className="text-green-600" size={20} />,
                bgColor: "bg-green-50",
              },
              {
                title: "Ventes",
                value: totalVente,
                icon: (
                  <BiShoppingBag className="text-secondary-600" size={20} />
                ),
                bgColor: "bg-secondary-50",
              },
              {
                title: "Réservations",
                value: totalAppointment,
                icon: <BiShoppingBag className="text-blue-600" size={20} />,
                bgColor: "bg-blue-50",
              },
              {
                title: "Abonnement",
                value: totalSubscription,
                icon: <BiShoppingBag className="text-amber-600" size={20} />,
                bgColor: "bg-amber-50",
              },

              {
                title: "Paiement Cash",
                value: totalCash,
                icon: <BiMoney className="text-green-600" size={20} />,
                bgColor: "bg-green-50",
              },
              {
                title: "Paiement Momo",
                value: totalMomo,
                icon: <TbPigMoney className="text-secondary-600" size={20} />,
                bgColor: "bg-secondary-50",
              },
              {
                title: "Paiement Fedapay",
                value: totalFedapay,
                icon: <TbMoneybag className="text-blue-600" size={20} />,
                bgColor: "bg-blue-50",
              },

              // {
              //   title: "Clients",
              //   value: totalClient,
              //   icon: <BiUser className="text-purple-600" size={20} />,
              //   bgColor: "bg-purple-50",
              // },
              // {
              //   title: "Autres clients",
              //   value: totalClientExtra,
              //   icon: <BiUserX className="text-orange-600" size={20} />,
              //   bgColor: "bg-orange-50",
              // },
            ].map((stat, index) => (
              <div
                key={index}
                className={`${stat.bgColor} border border-gray-200 p-4`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-nunito font-medium text-xs text-text mb-1">
                      {stat.title}
                    </p>
                    <p className="font-poppins font-semibold text-sm text-title">
                      {stat.value}
                    </p>
                  </div>
                  {stat.icon}
                </div>
              </div>
            ))}
          </div>

          {/* Barre de recherche */}
          <div className="bg-white border border-gray-200 p-4 mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1">
                <div className="relative max-w-md">
                  <BiSearch
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="Rechercher client, code, type..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-secondary-500 text-sm"
                  />
                </div>
              </div>
              <div className="font-nunito text-xs text-text">
                {filteredPayments.length} paiement
                {filteredPayments.length > 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mb-2 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} • {filteredPayments.length}{" "}
                élément{filteredPayments.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Tableau Desktop */}
          <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column, index) => (
                      <th
                        key={index}
                        className="px-4 py-3 text-left font-poppins font-bold text-xs text-title border-b border-gray-200"
                        style={{ width: column.width }}
                      >
                        {column.headerName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y font-poppins divide-gray-200">
                  {paginatedPayments.map((payment, index) => (
                    <tr
                      key={payment.id}
                      className={
                        index % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className="px-4 py-3">
                          <div className="flex items-center h-full">
                            {column.render(payment)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cartes Mobile */}
          <div className="lg:hidden space-y-3 mb-6">
            {paginatedPayments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
              <div className="font-nunito text-xs text-text">
                Page {currentPage} sur {totalPages} • {filteredPayments.length}{" "}
                élément{filteredPayments.length > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 text-xs font-nunito ${
                        currentPage === pageNum
                          ? "bg-secondary-600 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <BiChevronRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Message vide */}
          {filteredPayments.length === 0 && (
            <div className="text-center py-12 bg-white border border-gray-200">
              <BiShoppingBag className="mx-auto text-gray-300" size={40} />
              <p className="font-nunito text-sm text-text mt-3">
                {searchTerm
                  ? "Aucun résultat trouvé"
                  : "Aucune vente pour cette date"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de détails */}
      <PaymentDetailModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
    </div>
  );
}
